/**
 * System Health (Step 6) — observability for the platform's shared infrastructure:
 * background jobs, the dead-letter queue and failed event deliveries.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertOctagon, RefreshCw, Play, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import useAuthStore from '../lib/auth-store';

// Fixed order so the counters do not reshuffle on every refresh.
const STATUS_ORDER = ['queued', 'running', 'failed', 'dead', 'succeeded', 'cancelled'];

const STATUS_TONE = {
  succeeded: 'text-emerald-600 bg-emerald-500/10',
  queued: 'text-sky-600 bg-sky-500/10',
  running: 'text-violet-600 bg-violet-500/10',
  failed: 'text-amber-600 bg-amber-500/10',
  dead: 'text-rose-600 bg-rose-500/10',
  cancelled: 'text-muted-foreground bg-muted',
};

const DEAD_REASONS = {
  max_attempts_exhausted: 'Retries exhausted',
  non_retryable: 'Non-retryable error',
  unregistered_job_type: 'No handler (non-retryable)',
};

export default function SystemHealthPage() {
  const { user } = useAuthStore();
  const isAdmin = ['super_admin', 'iso'].includes(user?.role);
  const [stats, setStats] = useState({ by_status: {}, dead_letter_by_type: {}, handlers: [] });
  const [dlq, setDlq] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [s, d, f] = await Promise.all([
        api.getJobStats(),
        api.getDeadLetterQueue(),
        api.getEventFailures(),
      ]);
      setStats(s);
      setDlq(d.jobs || []);
      setFailures(f.failures || []);
    } catch (e) {
      toast.error('Could not load system health');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    try {
      const result = await api.runDueJobs();
      toast.success(`Worker pass complete — ${result.succeeded || 0} succeeded, ${result.dead || 0} dead-lettered`);
      load();
    } catch (e) {
      toast.error('Could not run the worker pass');
    }
  };

  const requeue = async (jobId) => {
    try {
      await api.requeueJob(jobId);
      toast.success('Job requeued');
      load();
    } catch (e) {
      toast.error('Could not requeue that job');
    }
  };

  const retryEvents = async () => {
    try {
      const result = await api.retryFailedEventDeliveries();
      toast.success(`${result.recovered} of ${result.retried} deliveries recovered`);
      load();
    } catch (e) {
      toast.error('Could not retry the failed deliveries');
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-dashed border-border p-12 text-center"
           data-testid="system-health-forbidden">
        <h1 className="text-base md:text-lg font-semibold">Admin only</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Platform jobs and retries are managed by your ISO administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="system-health-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-primary" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">System Health</h1>
          </div>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
            Background work, retries and anything that gave up. Nothing fails silently.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-health-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={runNow} data-testid="run-jobs-btn">
            <Play className="h-4 w-4 mr-2" /> Run due jobs
          </Button>
        </div>
      </header>

      <section className="flex flex-wrap gap-3" data-testid="job-status-cards">
        {STATUS_ORDER.filter((s) => (stats.by_status || {})[s] !== undefined)
          .map((status) => [status, stats.by_status[status]])
          .map(([status, count]) => (
          <div
            key={status}
            className="rounded-xl border border-border px-4 py-3 min-w-[140px]"
            data-testid={`job-status-${status}`}
          >
            <span className="block text-2xl font-semibold leading-none">{count}</span>
            <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_TONE[status] || STATUS_TONE.cancelled}`}>
              {status}
            </span>
          </div>
        ))}
        {!loading && Object.keys(stats.by_status || {}).length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="no-jobs-yet">
            No background jobs have run yet.
          </p>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <AlertOctagon className="h-5 w-5 text-rose-600" />
          <h2 className="text-base md:text-lg font-semibold">Dead-letter queue</h2>
          <span className="text-sm text-muted-foreground">({dlq.length})</span>
        </div>
        {dlq.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="dlq-empty">
            <p className="text-sm text-muted-foreground">Nothing has exhausted its retries. </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border" data-testid="dlq-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Job</th>
                  <th className="px-4 py-3 text-left">Attempts</th>
                  <th className="px-4 py-3 text-left">Why dead</th>
                  <th className="px-4 py-3 text-left">Last error</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {dlq.map((job) => (
                  <tr key={job.job_id} className="border-t border-border" data-testid={`dlq-row-${job.job_id}`}>
                    <td className="px-4 py-3 font-medium">{job.job_type}</td>
                    <td className="px-4 py-3 text-muted-foreground">{job.attempts}/{job.max_attempts}</td>
                    <td className="px-4 py-3" data-testid={`dlq-reason-${job.job_id}`}>
                      <span className="text-xs rounded-full px-2 py-0.5 bg-muted text-foreground">
                        {DEAD_REASONS[job.dead_reason] || job.dead_reason || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground max-w-md truncate" title={job.last_error}>
                      {job.last_error}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => requeue(job.job_id)}
                              data-testid={`requeue-${job.job_id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Requeue
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base md:text-lg font-semibold">Failed event deliveries</h2>
          <span className="text-sm text-muted-foreground">({failures.length})</span>
          {failures.length > 0 && (
            <Button size="sm" variant="outline" onClick={retryEvents} data-testid="retry-events-btn">
              Retry all
            </Button>
          )}
        </div>
        {failures.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="event-failures-empty">
            <p className="text-sm text-muted-foreground">Every consumer is keeping up.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="event-failures-list">
            {failures.map((f) => (
              <li key={`${f.event_id}-${f.consumer}`}
                  className="rounded-lg border border-border px-4 py-3 text-sm"
                  data-testid={`event-failure-${f.event_id}`}>
                <span className="font-medium">{f.consumer}</span>
                <span className="text-muted-foreground"> · {f.event_type} · attempt {f.retry_count}</span>
                <p className="text-xs text-muted-foreground mt-1">{f.last_error}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-semibold">Registered job handlers</h2>
        <div className="flex flex-wrap gap-2" data-testid="job-handlers">
          {(stats.handlers || []).map((h) => (
            <span key={h.job_type} className="rounded-full border border-border px-3 py-1 text-xs font-mono">
              {h.job_type} · {h.max_attempts} attempts
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
